import { ChevronLeft, ChevronRight } from 'lucide-react';

import { pageWindow } from './page-window';
import s from './Pagination.module.css';

interface PaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Accessible name for the <nav> landmark. Required — a page can hold more
   *  than one paginator (drafts + archived drafts) and unnamed duplicate
   *  landmarks leave screen-reader users guessing which is which. */
  label: string;
  /** e.g. "11–20 of 43 drafts" — announced politely as the page changes. */
  summary?: string | undefined;
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  label,
  summary,
}: PaginationProps): JSX.Element | null {
  // A one-page control is noise: it can never do anything.
  if (pageCount <= 1) return null;

  const slots = pageWindow(page, pageCount);

  return (
    <div className={s.root}>
      {summary !== undefined ? (
        <p className={s.summary} aria-live="polite">
          {summary}
        </p>
      ) : null}

      <nav className={s.nav} aria-label={label}>
        <button
          type="button"
          className={s.step}
          disabled={page <= 1}
          aria-label="Previous page"
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>

        <ul className={s.pages}>
          {slots.map((slot, i) =>
            slot === 'gap' ? (
              <li key={`gap-${String(i)}`} className={s.gap} aria-hidden="true">
                …
              </li>
            ) : (
              <li key={slot}>
                <button
                  type="button"
                  className={`${s.page ?? ''} ${slot === page ? (s.pageCurrent ?? '') : ''}`.trim()}
                  aria-label={`Page ${String(slot)}`}
                  aria-current={slot === page ? 'page' : undefined}
                  onClick={() => onPageChange(slot)}
                >
                  {slot}
                </button>
              </li>
            ),
          )}
        </ul>

        <button
          type="button"
          className={s.step}
          disabled={page >= pageCount}
          aria-label="Next page"
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </nav>
    </div>
  );
}
